"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createEntityStore = createEntityStore;
const zustand_1 = require("zustand");
const middleware_1 = require("zustand/middleware");
// 安全的 localStorage 包裝器
const safeLocalStorage = {
    getItem: (key) => {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem(key);
        }
        return null;
    },
    setItem: (key, value) => {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(key, value);
        }
    },
    removeItem: (key) => {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(key);
        }
    }
};
function createEntityStore(entityNameOrOptions, fetchFn, deleteFn) {
    // 處理兩種呼叫方式
    let entityName;
    let actualFetchFn;
    let actualDeleteFn;
    let messages;
    let toastFn;
    if (typeof entityNameOrOptions === 'string') {
        // 舊的呼叫方式：createEntityStore(entityName, fetchFn, deleteFn)
        entityName = entityNameOrOptions;
        actualFetchFn = fetchFn;
        actualDeleteFn = deleteFn;
    }
    else {
        // 新的呼叫方式：createEntityStore({ entityName, fetchFn, deleteFn, ... })
        entityName = entityNameOrOptions.entityName;
        actualFetchFn = entityNameOrOptions.fetchFn;
        actualDeleteFn = entityNameOrOptions.deleteFn;
        messages = entityNameOrOptions.messages;
        toastFn = entityNameOrOptions.toast;
    }
    return (0, zustand_1.create)()((0, middleware_1.persist)((set, get) => ({
        items: [],
        pagination: null,
        currentPage: 1,
        error: null,
        showDeleteConfirmModal: false,
        itemToDeleteId: null,
        isDeleting: false,
        isLoading: false,
        isPageLoading: false,
        lastOptimisticUpdate: undefined,
        fetchItems: async (page = 1, forceRefresh = false) => {
            const state = get();
            // 檢查分頁快取（如果不是強制刷新）
            const cacheKey = `${entityName}-page-${page}`;
            if (!forceRefresh && typeof localStorage !== 'undefined') {
                try {
                    const cached = safeLocalStorage.getItem(cacheKey);
                    if (cached) {
                        const cacheData = JSON.parse(cached);
                        // 快取永久有效，直到登出或手動刪除
                        // 檢查是否有樂觀更新，如果樂觀更新比快取新，就不要使用快取
                        const state = get();
                        const shouldUseCache = !state.lastOptimisticUpdate ||
                            state.lastOptimisticUpdate <= cacheData.timestamp; // 快取比樂觀更新新或相等
                        if (shouldUseCache) {
                            // 先顯示快取內容
                            set({
                                items: cacheData.items,
                                pagination: cacheData.pagination,
                                currentPage: page,
                                isLoading: false,
                                isPageLoading: false,
                            });
                            // 背景檢查是否有新資料（不阻塞UI）
                            setTimeout(async () => {
                                try {
                                    const response = await actualFetchFn(page);
                                    const newData = response.data;
                                    // 整頁內容比對：原本逐筆比對同位置的 id／updatedAt，中間有項目被別人刪除（後面往前補位）
                                    // 或關聯資料改名（updatedAt 不變）時偵測不到，畫面一直停在舊資料
                                    const hasChanges = JSON.stringify([newData.data, newData.pagination]) !==
                                        JSON.stringify([cacheData.items, cacheData.pagination]);
                                    if (hasChanges) {
                                        // 靜默更新資料和快取
                                        const updateData = {
                                            items: newData.data,
                                            pagination: newData.pagination,
                                            currentPage: page,
                                            isLoading: false,
                                            isPageLoading: false,
                                            lastOptimisticUpdate: undefined, // 清除樂觀更新時間戳，因為資料已同步
                                        };
                                        set(updateData);
                                        // 更新快取
                                        const newCacheData = {
                                            ...updateData,
                                            timestamp: Date.now()
                                        };
                                        safeLocalStorage.setItem(cacheKey, JSON.stringify(newCacheData));
                                    }
                                }
                                catch (error) {
                                    // 背景更新失敗不影響用戶體驗
                                    console.debug(`Background update failed for ${entityName} page ${page}:`, error);
                                }
                            }, 500);
                            // 觸發下一頁預載入
                            const { preloadNextPage } = get();
                            if (cacheData.pagination) {
                                setTimeout(() => {
                                    preloadNextPage(cacheData.pagination.currentPage, cacheData.pagination.totalPages);
                                }, 100);
                            }
                            return;
                        }
                        else {
                            // 有樂觀更新且比快取新，快取已過期
                            // 清除過期快取，繼續執行後續的立即載入邏輯
                            safeLocalStorage.removeItem(cacheKey);
                        }
                    }
                    else {
                        safeLocalStorage.removeItem(cacheKey);
                    }
                }
                catch (error) {
                    safeLocalStorage.removeItem(cacheKey);
                }
            }
            else {
                // 如果是強制刷新，清除所有相關快取
                try {
                    if (typeof localStorage !== 'undefined') {
                        const keys = Object.keys(localStorage);
                        keys.forEach(key => {
                            if (key.startsWith(`${entityName}-page-`)) {
                                safeLocalStorage.removeItem(key);
                            }
                        });
                    }
                }
                catch (error) {
                    console.warn('Failed to clear cache:', error);
                }
            }
            // 設定載入狀態
            const isFirstLoad = state.items.length === 0;
            // 如果有樂觀更新標記，表示剛做完批量操作，不顯示載入骨架
            const hasRecentOptimisticUpdate = state.lastOptimisticUpdate !== undefined;
            set({
                error: null,
                isLoading: isFirstLoad && !hasRecentOptimisticUpdate,
                isPageLoading: !isFirstLoad && !hasRecentOptimisticUpdate
            });
            try {
                const response = await actualFetchFn(page);
                const fetchedData = {
                    items: response.data.data,
                    pagination: response.data.pagination,
                    currentPage: page,
                    isLoading: false,
                    isPageLoading: false,
                };
                set({
                    ...fetchedData,
                    lastOptimisticUpdate: undefined, // 清除樂觀更新時間戳，因為資料已同步
                });
                // 設置分頁快取
                try {
                    const cacheData = {
                        ...fetchedData,
                        timestamp: Date.now()
                    };
                    safeLocalStorage.setItem(cacheKey, JSON.stringify(cacheData));
                }
                catch (error) {
                    console.warn(`Failed to cache ${entityName} page data:`, error);
                }
                // 觸發下一頁預載入（背景執行）
                const { preloadNextPage } = get();
                if (fetchedData.pagination) {
                    setTimeout(() => {
                        preloadNextPage(fetchedData.pagination.currentPage, fetchedData.pagination.totalPages);
                    }, 100); // 延遲100ms執行，確保當前頁面載入完成
                }
            }
            catch (err) {
                // 顯示錯誤訊息（支援函數或字串）
                const rawFetchError = messages?.fetchError;
                let errorMessage;
                if (rawFetchError) {
                    errorMessage = typeof rawFetchError === 'function' ? rawFetchError() : rawFetchError;
                }
                else if (err.response?.data?.error) {
                    errorMessage = err.response.data.error;
                }
                else if (err.response?.data?.message) {
                    errorMessage = err.response.data.message;
                }
                else {
                    errorMessage = err.message || 'Fetch failed';
                }
                set({
                    error: errorMessage,
                    isLoading: false,
                    isPageLoading: false
                });
            }
        },
        setCurrentPage: (page) => {
            const { fetchItems } = get();
            set({ currentPage: page });
            fetchItems(page).then(() => {
                // fetchItems 成功後會自動觸發預載入，這裡不需要額外處理
            }).catch(() => {
                // fetchItems 失敗時也不需要處理預載入
            });
        },
        handleDelete: (id) => {
            set({ itemToDeleteId: id, showDeleteConfirmModal: true });
        },
        confirmDelete: async () => {
            const state = get();
            const { itemToDeleteId } = state;
            if (!itemToDeleteId)
                return;
            // 立即保存原始狀態，用於錯誤時復原
            const originalItems = Array.isArray(state.items) ? [...state.items] : [];
            const originalPagination = state.pagination ? { ...state.pagination } : null;
            // 同時關閉視窗並移除項目
            set({
                showDeleteConfirmModal: false,
                itemToDeleteId: null,
                isDeleting: false,
                items: (Array.isArray(state.items) ? state.items : []).filter(item => item.id !== itemToDeleteId),
                pagination: state.pagination ? {
                    ...state.pagination,
                    totalItems: Math.max(0, state.pagination.totalItems - 1)
                } : null,
                lastOptimisticUpdate: Date.now(), // 記錄樂觀更新時間
            });
            try {
                // 背景執行刪除 API
                await actualDeleteFn(itemToDeleteId);
                // 顯示成功訊息（支援函數或字串）
                const rawMessage = messages?.deleteSuccess;
                if (rawMessage) {
                    const successMessage = typeof rawMessage === 'function' ? rawMessage() : rawMessage;
                    toastFn?.success(successMessage);
                }
                // 不清除分頁快取，避免切換頁面後回來時顯示骨架
                // lastOptimisticUpdate 已經設置，快取系統會自動處理更新
            }
            catch (err) {
                // 失敗時恢復原始狀態
                set({
                    items: originalItems,
                    pagination: originalPagination,
                    error: err.message || 'Delete failed'
                });
                // 顯示錯誤訊息（優先使用自定義訊息，否則解析伺服器錯誤）
                const rawErrorMessage = messages?.deleteError;
                let errorMessage;
                if (rawErrorMessage) {
                    errorMessage = typeof rawErrorMessage === 'function' ? rawErrorMessage() : rawErrorMessage;
                }
                else if (err.response?.data?.error) {
                    errorMessage = err.response.data.error;
                }
                else if (err.response?.data?.message) {
                    errorMessage = err.response.data.message;
                }
                else {
                    errorMessage = err.message || 'Delete failed';
                }
                toastFn?.error(errorMessage);
            }
        },
        closeDeleteModal: () => {
            set({ showDeleteConfirmModal: false, itemToDeleteId: null, isDeleting: false });
        },
        refreshData: async () => {
            const { currentPage, fetchItems } = get();
            await fetchItems(currentPage, true); // 強制刷新
        },
        updateItem: (id, updatedData) => {
            set((state) => ({
                items: (Array.isArray(state.items) ? state.items : []).map(item => item.id === id ? { ...item, ...updatedData } : item),
                lastOptimisticUpdate: Date.now(),
            }));
        },
        addItem: (newItem) => {
            set((state) => ({
                items: [newItem, ...(Array.isArray(state.items) ? state.items : [])],
                pagination: state.pagination ? {
                    ...state.pagination,
                    totalItems: state.pagination.totalItems + 1
                } : null,
                lastOptimisticUpdate: Date.now(),
            }));
        },
        getItemById: (id) => {
            const { items } = get();
            return (Array.isArray(items) ? items : []).find(item => item.id === id);
        },
        clearError: () => {
            set({ error: null });
        },
        clearCache: () => {
            try {
                // 清除所有相關的分頁快取（包括預載入快取）
                if (typeof localStorage !== 'undefined') {
                    const keys = Object.keys(localStorage);
                    const cacheKeys = keys.filter(key => key.startsWith(`${entityName}-page-`) || key.startsWith(`${entityName}-preload-`));
                    cacheKeys.forEach(key => safeLocalStorage.removeItem(key));
                }
            }
            catch (error) {
                console.warn(`Failed to clear cache for ${entityName}:`, error);
            }
        },
        // invalidateCache 是 clearCache 的別名，為了與其他 store 的命名保持一致
        invalidateCache: function () {
            this.clearCache();
        },
        preloadNextPage: async (currentPage, totalPages) => {
            // 如果已經是最後一頁，不預載入
            if (currentPage >= totalPages) {
                return;
            }
            const nextPage = currentPage + 1;
            const preloadKey = `${entityName}-preload-${nextPage}`;
            // 檢查是否已經預載入過（2分鐘快取）
            try {
                const cached = safeLocalStorage.getItem(preloadKey);
                if (cached) {
                    const cacheData = JSON.parse(cached);
                    // 預載入快取永久有效，直到登出或手動刪除
                    return; // 已經預載入過，跳過
                }
            }
            catch (error) {
                safeLocalStorage.removeItem(preloadKey);
            }
            // 背景預載入下一頁
            try {
                const response = await actualFetchFn(nextPage);
                const preloadData = {
                    items: response.data.data,
                    pagination: response.data.pagination,
                    timestamp: Date.now(),
                    isPreloaded: true
                };
                // 儲存預載入資料
                safeLocalStorage.setItem(preloadKey, JSON.stringify(preloadData));
                // 同時也儲存到一般快取中（這樣切換頁面時會立即顯示）
                const normalCacheKey = `${entityName}-page-${nextPage}`;
                const normalCacheData = {
                    items: response.data.data,
                    pagination: response.data.pagination,
                    currentPage: nextPage,
                    isLoading: false,
                    isPageLoading: false,
                    timestamp: Date.now()
                };
                safeLocalStorage.setItem(normalCacheKey, JSON.stringify(normalCacheData));
            }
            catch (error) {
                // 預載入失敗時靜默忽略，不影響用戶體驗
                console.debug(`Preload failed for ${entityName} page ${nextPage}:`, error);
            }
        },
    }), {
        name: `${entityName}-storage`,
        partialize: (state) => ({
            items: Array.isArray(state.items) ? state.items : [],
            pagination: state.pagination,
            currentPage: state.currentPage,
            lastOptimisticUpdate: state.lastOptimisticUpdate,
        }),
        // 從存儲恢復時確保數據類型正確
        merge: (persistedState, currentState) => {
            // 如果當前狀態有較新的樂觀更新，不應該被舊的 persist 狀態覆蓋
            const shouldUsePersistedState = !currentState.lastOptimisticUpdate ||
                (persistedState?.lastOptimisticUpdate && persistedState.lastOptimisticUpdate >= currentState.lastOptimisticUpdate);
            if (shouldUsePersistedState) {
                return {
                    ...currentState,
                    ...persistedState,
                    items: Array.isArray(persistedState?.items) ? persistedState.items : [],
                };
            }
            else {
                // 當前狀態有較新的樂觀更新，保留當前狀態
                return currentState;
            }
        },
    }));
}
